import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import api from '@/lib/api';

function formatError(detail) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(' ');
  return 'Something went wrong. Please try again.';
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'register' ? 'register' : 'login';

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Register state
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regFirst, setRegFirst] = useState('');
  const [regLast, setRegLast] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      await login(loginEmail, loginPassword);
      const redirect = searchParams.get('redirect');
      navigate(redirect || '/dashboard');
    } catch (err) {
      setLoginError(formatError(err.response?.data?.detail) || 'Login failed.');
    } finally { setLoginLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    if (regPassword.length < 6) { setRegError('Password must be at least 6 characters'); return; }
    setRegLoading(true);
    try {
      await api.post('/auth/register', {
        email: regEmail, password: regPassword, first_name: regFirst, last_name: regLast, phone: regPhone || null
      });
      setRegSuccess(true);
    } catch (err) {
      setRegError(formatError(err.response?.data?.detail) || 'Registration failed.');
    } finally { setRegLoading(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1757262798620-c2cc40cfb440?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwbGl2aW5nJTIwcm9vbSUyMGJyaWdodHxlbnwwfHx8fDE3NzU5OTk3MjV8MA&ixlib=rb-4.1.0&q=85"
          alt="Modern living room"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-4xl font-bold font-heading tracking-tight mb-3">Manage your rentals with clarity</h2>
          <p className="text-lg text-white/80 max-w-md">Streamline bookings, guests, and operations in one powerful platform.</p>
        </div>
      </div>

      {/* Right - Auth Forms */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="relative mx-auto mb-8" style={{ width: 'fit-content' }}>
            <img src="https://customer-assets.emergentagent.com/job_mongo-property-app/artifacts/xfew4ztu_06B377E7-711C-4306-B7DD-5FE7F88F775D.png" alt="RentalHub" className="object-contain mx-auto block" style={{ width: '100%', maxWidth: '1400px', height: 'auto' }} />
            <span className="absolute text-[0.6em] font-semibold text-slate-400 select-none" style={{ top: '38%', right: '15%' }}>TM</span>
          </div>

          <Tabs defaultValue={initialTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="auth-tab-login">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="auth-tab-register">Create Account</TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <h3 className="text-2xl font-semibold font-heading text-slate-900 mb-1">Welcome back</h3>
              <p className="text-sm text-slate-500 mb-6">Sign in to your account</p>

              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 text-sm" data-testid="login-error">{loginError}</div>
              )}

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-sm font-medium text-slate-700">Email</Label>
                  <Input id="login-email" type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" required data-testid="login-email-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password" className="text-sm font-medium text-slate-700">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-slate-700 font-medium" data-testid="forgot-password-link">Forgot password?</Link>
                  </div>
                  <div className="relative mt-1.5">
                    <Input id="login-password" type={showPassword ? 'text' : 'password'} value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="Enter your password" className="pr-10" required data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="toggle-password-visibility">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" disabled={loginLoading} data-testid="login-submit-btn">
                  {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Sign in
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/staff-register" className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-testid="staff-register-link">Have a staff code? Register here</Link>
              </div>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              <h3 className="text-2xl font-semibold font-heading text-slate-900 mb-1">Create admin account</h3>
              <p className="text-sm text-slate-500 mb-6">Set up your property management account</p>

              {regSuccess ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">Check your email</h4>
                  <p className="text-sm text-slate-500 mb-4">We've sent a verification link to <strong>{regEmail}</strong>. Please verify your email to activate your account.</p>
                  <p className="text-xs text-slate-400">Didn't receive it? Check your spam folder.</p>
                </div>
              ) : (
                <>
                  {regError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 text-sm" data-testid="register-error">{regError}</div>
                  )}

                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm font-medium text-slate-700">First Name</Label>
                        <Input value={regFirst} onChange={e => setRegFirst(e.target.value)} placeholder="First" className="mt-1.5" required data-testid="register-firstname-input" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Last Name</Label>
                        <Input value={regLast} onChange={e => setRegLast(e.target.value)} placeholder="Last" className="mt-1.5" required data-testid="register-lastname-input" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Email</Label>
                      <Input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" required data-testid="register-email-input" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Phone (optional)</Label>
                      <Input value={regPhone} onChange={e => setRegPhone(e.target.value)} placeholder="+27 ..." className="mt-1.5" data-testid="register-phone-input" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Password</Label>
                      <Input type="password" value={regPassword} onChange={e => setRegPassword(e.target.value)} placeholder="Min 6 characters" className="mt-1.5" required data-testid="register-password-input" />
                    </div>
                    <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" disabled={regLoading} data-testid="register-submit-btn">
                      {regLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Create Account
                    </Button>
                  </form>

                  <p className="mt-4 text-xs text-slate-400 text-center">A verification email will be sent to confirm your address</p>
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
